import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpenText,
  Clock3,
  Filter,
  Layers3,
  Loader2,
  MessageSquareQuote,
  Search,
  UserRound,
} from 'lucide-react';

import { useAuth } from '../services/AuthContext';
import { useShareLink } from '../contexts/ShareLinkContext';
import * as questionService from '../services/questionService';
import type { SessionQuestion } from '../services/questionService';

interface QuestionsTabProps {
  sessionId: string;
  isDarkMode?: boolean;
  onSeek?: (seconds: number) => void;
}

type ViewMode = 'time' | 'topic';

function formatTimestamp(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return 'Untimed';

  const rounded = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  const remainingSeconds = rounded % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }

  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

function formatUpdatedAt(value: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function sortQuestions(questions: SessionQuestion[]): SessionQuestion[] {
  return [...questions].sort((a, b) => {
    const aTimestamp = a.timestampSeconds;
    const bTimestamp = b.timestampSeconds;

    if (aTimestamp !== null && bTimestamp !== null && aTimestamp !== bTimestamp) {
      return aTimestamp - bTimestamp;
    }
    if (aTimestamp !== null && bTimestamp === null) return -1;
    if (aTimestamp === null && bTimestamp !== null) return 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    if (a.createdAt !== b.createdAt) return a.createdAt.localeCompare(b.createdAt);
    return a.questionText.localeCompare(b.questionText);
  });
}

function groupQuestionsByTopic(questions: SessionQuestion[]): Array<{ topic: string; questions: SessionQuestion[] }> {
  const groups = new Map<string, SessionQuestion[]>();
  for (const question of questions) {
    const key = question.topic?.trim() || 'Uncategorized';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)?.push(question);
  }

  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([topic, groupedQuestions]) => ({ topic, questions: sortQuestions(groupedQuestions) }));
}

function QuestionCard({
  question,
  isDarkMode,
  onSeek,
}: {
  question: SessionQuestion;
  isDarkMode: boolean;
  onSeek?: (seconds: number) => void;
}) {
  const surfaceClass = isDarkMode
    ? 'border-[#39465A] bg-[#182130]/90 text-[#E7ECF2]'
    : 'border-[#D8DEE7] bg-white text-[#232E3F]';

  const metaPillClass = isDarkMode
    ? 'bg-white/8 text-[#D5DEEA]'
    : 'bg-[#F3F6FA] text-[#4E5D72]';

  const subtleTextClass = isDarkMode ? 'text-[#A7B2C4]' : 'text-[#66758A]';

  const canSeek = question.timestampSeconds !== null && Number.isFinite(question.timestampSeconds);

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${surfaceClass}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (canSeek && onSeek) {
                onSeek(question.timestampSeconds as number);
              }
            }}
            disabled={!canSeek || !onSeek}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition ${
              !canSeek || !onSeek
                ? `${metaPillClass} cursor-default`
                : isDarkMode
                  ? 'bg-[#FFB52B]/14 text-[#FFE3A1] hover:bg-[#FFB52B]/22'
                  : 'bg-[#FFF4D9] text-[#8A6100] hover:bg-[#FFE7AD]'
            }`}
            title={canSeek && onSeek ? 'Jump to this question on the timeline' : 'No timestamp recorded'}
          >
            <Clock3 size={12} />
            {question.timecode || formatTimestamp(question.timestampSeconds)}
          </button>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${metaPillClass}`}>
            <MessageSquareQuote size={12} />
            {question.questionType === 'implicit' ? 'Implicit' : 'Explicit'}
          </span>
          {question.askedBy && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${metaPillClass}`}>
              <UserRound size={12} />
              {question.askedBy}
            </span>
          )}
          {question.topic && (
            <span className={`rounded-full px-2.5 py-1 text-xs ${metaPillClass}`}>
              {question.topic}
            </span>
          )}
        </div>

        <p className="mt-3 whitespace-pre-wrap text-sm leading-6 sm:text-[15px]">{question.questionText}</p>

        {question.answerSummary && (
          <div className={`mt-3 rounded-xl px-3 py-2 text-xs leading-5 ${isDarkMode ? 'bg-[#142033] text-[#E0E7F1]' : 'bg-[#EEF5FF] text-[#35506C]'}`}>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.14em] opacity-80">Answer Summary</div>
            {question.answerSummary}
          </div>
        )}

        {question.contextText && (
          <div className={`mt-3 rounded-xl px-3 py-2 text-xs leading-5 ${isDarkMode ? 'bg-white/6 text-[#CDD6E2]' : 'bg-[#F5F7FA] text-[#546276]'}`}>
            {question.contextText}
          </div>
        )}

        {question.updatedAt && (
          <div className={`mt-3 text-xs ${subtleTextClass}`}>
            Updated {formatUpdatedAt(question.updatedAt)}
          </div>
        )}
      </div>
    </div>
  );
}

const QuestionsTab: React.FC<QuestionsTabProps> = ({ sessionId, isDarkMode = false, onSeek }) => {
  const { token } = useAuth();
  const { permissions: sharePermissions, shareToken } = useShareLink();
  const [questions, setQuestions] = useState<SessionQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('time');
  const [searchQuery, setSearchQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState('all');
  const [questionTypeFilter, setQuestionTypeFilter] = useState<'all' | 'explicit' | 'implicit'>('all');

  const loadQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const response = sharePermissions?.isPublicShare && shareToken
        ? await questionService.listPublicShareQuestions(shareToken)
        : await questionService.listQuestions(sessionId, token || undefined);
      setQuestions(sortQuestions(response.questions));
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [sessionId, sharePermissions?.isPublicShare, shareToken, token]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const visibleQuestions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return sortQuestions(
      questions.filter((question) => {
        if (topicFilter !== 'all' && (question.topic || 'Uncategorized') !== topicFilter) {
          return false;
        }
        if (questionTypeFilter !== 'all' && question.questionType !== questionTypeFilter) {
          return false;
        }
        if (!normalizedQuery) return true;
        const haystack = [
          question.questionText,
          question.askedBy,
          question.topic,
          question.contextText,
          question.answerSummary,
          question.timecode,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      })
    );
  }, [questions, questionTypeFilter, searchQuery, topicFilter]);

  const groupedQuestions = useMemo(() => groupQuestionsByTopic(visibleQuestions), [visibleQuestions]);

  const questionsWithTimestamps = useMemo(
    () => questions.filter((question) => question.timestampSeconds !== null).length,
    [questions]
  );

  const distinctTopics = useMemo(
    () => [...new Set(questions.map((question) => question.topic?.trim() || 'Uncategorized'))].sort((a, b) => a.localeCompare(b)),
    [questions]
  );

  const implicitCount = useMemo(
    () => questions.filter((question) => question.questionType === 'implicit').length,
    [questions]
  );

  const pageSurfaceClass = isDarkMode
    ? 'bg-[#111826] text-[#E7ECF2]'
    : 'bg-[#F4F4F4] text-[#232E3F]';
  const panelClass = isDarkMode
    ? 'border-[#39465A] bg-[#182130]/92'
    : 'border-[#D8DEE7] bg-white';

  return (
    <div className={`flex h-full flex-col overflow-hidden ${pageSurfaceClass}`}>
      <div className="overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
          <div className={`rounded-[28px] border p-6 shadow-sm ${panelClass}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <div className={`text-xs font-semibold uppercase tracking-[0.22em] ${isDarkMode ? 'text-[#AAB7C8]' : 'text-[#6B7A90]'}`}>
                  Episode Capture
                </div>
                <h2 className="mt-2 text-2xl font-semibold">Questions Asked</h2>
                <p className={`mt-2 max-w-3xl text-sm leading-6 ${isDarkMode ? 'text-[#B9C5D6]' : 'text-[#5E6B7D]'}`}>
                  This is a browse-and-discovery view for questions that were actually asked or implicitly answered in the episode. Records are expected to be created by agents through the CLI or API.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-4">
                <div className={`rounded-2xl px-4 py-3 text-sm ${isDarkMode ? 'bg-white/6 text-[#D5DEEA]' : 'bg-[#F5F7FA] text-[#546276]'}`}>
                  <div className="text-xs uppercase tracking-[0.16em] opacity-75">Total</div>
                  <div className="mt-1 text-lg font-semibold">{questions.length}</div>
                </div>
                <div className={`rounded-2xl px-4 py-3 text-sm ${isDarkMode ? 'bg-white/6 text-[#D5DEEA]' : 'bg-[#F5F7FA] text-[#546276]'}`}>
                  <div className="text-xs uppercase tracking-[0.16em] opacity-75">Timed</div>
                  <div className="mt-1 text-lg font-semibold">{questionsWithTimestamps}</div>
                </div>
                <div className={`rounded-2xl px-4 py-3 text-sm ${isDarkMode ? 'bg-white/6 text-[#D5DEEA]' : 'bg-[#F5F7FA] text-[#546276]'}`}>
                  <div className="text-xs uppercase tracking-[0.16em] opacity-75">Topics</div>
                  <div className="mt-1 text-lg font-semibold">{distinctTopics.length}</div>
                </div>
                <div className={`rounded-2xl px-4 py-3 text-sm ${isDarkMode ? 'bg-white/6 text-[#D5DEEA]' : 'bg-[#F5F7FA] text-[#546276]'}`}>
                  <div className="text-xs uppercase tracking-[0.16em] opacity-75">Implicit</div>
                  <div className="mt-1 text-lg font-semibold">{implicitCount}</div>
                </div>
              </div>
            </div>
          </div>

          <div className={`rounded-[28px] border p-4 shadow-sm ${panelClass}`}>
            <div className={`flex flex-col gap-4 rounded-2xl px-4 py-4 ${isDarkMode ? 'bg-white/6 text-[#D5DEEA]' : 'bg-[#F5F7FA] text-[#546276]'}`}>
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="inline-flex max-w-full rounded-2xl border p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setViewMode('time')}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${
                      viewMode === 'time'
                        ? 'bg-[#FFB52B] text-[#232E3F]'
                        : isDarkMode
                          ? 'text-[#D5DEEA] hover:bg-white/8'
                          : 'text-[#546276] hover:bg-white'
                    }`}
                  >
                    <Clock3 size={15} />
                    Time Order
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('topic')}
                    className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium ${
                      viewMode === 'topic'
                        ? 'bg-[#FFB52B] text-[#232E3F]'
                        : isDarkMode
                          ? 'text-[#D5DEEA] hover:bg-white/8'
                          : 'text-[#546276] hover:bg-white'
                    }`}
                  >
                    <Layers3 size={15} />
                    Group by Topic
                  </button>
                </div>

                <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_220px_220px] xl:items-center">
                  <label className={`inline-flex min-w-0 items-center gap-2 rounded-2xl border px-3 py-2 text-sm ${isDarkMode ? 'border-[#39465A] bg-[#111826]' : 'border-[#D8DEE7] bg-white'}`}>
                    <Search size={16} className="shrink-0" />
                    <input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search questions, topics, context..."
                      className="min-w-0 flex-1 bg-transparent outline-none"
                    />
                  </label>

                  <label className={`inline-flex min-w-0 items-center gap-2 rounded-2xl border px-3 py-2 text-sm ${isDarkMode ? 'border-[#39465A] bg-[#111826]' : 'border-[#D8DEE7] bg-white'}`}>
                    <Filter size={16} className="shrink-0" />
                    <select
                      value={topicFilter}
                      onChange={(event) => setTopicFilter(event.target.value)}
                      className="min-w-0 flex-1 bg-transparent outline-none"
                    >
                      <option value="all">All topics</option>
                      {distinctTopics.map((topic) => (
                        <option key={topic} value={topic}>
                          {topic}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={`inline-flex min-w-0 items-center gap-2 rounded-2xl border px-3 py-2 text-sm ${isDarkMode ? 'border-[#39465A] bg-[#111826]' : 'border-[#D8DEE7] bg-white'}`}>
                    <BookOpenText size={16} className="shrink-0" />
                    <select
                      value={questionTypeFilter}
                      onChange={(event) => setQuestionTypeFilter(event.target.value as typeof questionTypeFilter)}
                      className="min-w-0 flex-1 bg-transparent outline-none"
                    >
                      <option value="all">All question types</option>
                      <option value="explicit">Explicit only</option>
                      <option value="implicit">Implicit only</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="text-sm leading-6">
                Browse captured questions by time or topic. Implicit questions capture moments where the guest answers a question that was never explicitly asked on air.
              </div>
            </div>
          </div>

          <div className={`rounded-[28px] border p-4 shadow-sm ${panelClass}`}>
            {loading ? (
              <div className="flex min-h-[300px] items-center justify-center">
                <div className={`inline-flex items-center gap-3 rounded-2xl px-4 py-3 text-sm ${
                  isDarkMode ? 'bg-white/6 text-[#D5DEEA]' : 'bg-[#F5F7FA] text-[#546276]'
                }`}>
                  <Loader2 size={18} className="animate-spin" />
                  Loading questions…
                </div>
              </div>
            ) : visibleQuestions.length === 0 ? (
              <div className={`flex min-h-[300px] items-center justify-center rounded-[24px] border border-dashed text-center ${
                isDarkMode ? 'border-[#39465A] text-[#AAB7C8]' : 'border-[#D8DEE7] text-[#6B7A90]'
              }`}>
                <div className="max-w-sm px-6 py-10">
                  <div className="text-lg font-semibold">No captured questions yet</div>
                  <p className="mt-2 text-sm leading-6">
                    Questions will appear here once an agent populates them through the CLI or API.
                  </p>
                </div>
              </div>
            ) : viewMode === 'time' ? (
              <div className="space-y-3">
                {visibleQuestions.map((question) => (
                  <QuestionCard
                    key={question.id}
                    question={question}
                    isDarkMode={isDarkMode}
                    onSeek={onSeek}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-6">
                {groupedQuestions.map((group) => (
                  <section key={group.topic} className="space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.16em]">{group.topic}</h3>
                      <div className={`text-xs ${isDarkMode ? 'text-[#AAB7C8]' : 'text-[#6B7A90]'}`}>
                        {group.questions.length} question{group.questions.length === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="space-y-3">
                      {group.questions.map((question) => (
                        <QuestionCard
                          key={question.id}
                          question={question}
                          isDarkMode={isDarkMode}
                          onSeek={onSeek}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className={`rounded-2xl border px-4 py-3 text-sm ${
              isDarkMode ? 'border-[#7A2E2E] bg-[#3A1717] text-[#FFB8B8]' : 'border-[#E0A39A] bg-[#FDEDEA] text-[#8E2F25]'
            }`}>
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionsTab;
